import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import type { IconCardWidgetResolved } from './types';
import { Collapsible } from '@/components/Collapsible';
import { Icon } from '@/components/Icon';
import { useSubstitute } from '@/lib/render/RenderContextProvider';
import { sanitize } from '@/lib/sanitize';
import { getSectionColorClass } from '@/lib/section-colors';

enum IconCardColumns {
  One = 'grid-cols-1',
  Two = 'grid-cols-2',
  Three = 'grid-cols-3',
  Four = 'grid-cols-4',
}

const COLS = [
  IconCardColumns.One,
  IconCardColumns.Two,
  IconCardColumns.Three,
  IconCardColumns.Four,
];

export const IconCardWidgetComponent = ({
  resolved,
}: {
  resolved: IconCardWidgetResolved;
}) => {
  const sub = useSubstitute();
  const items = resolved.items ?? [];
  if (items.length === 0) {
    return null;
  }

  const colsClass =
    COLS[Math.min(resolved.columns, items.length) - 1] ?? IconCardColumns.One;

  return (
    <section className="pt-4">
      <div>
        <div className={`hidden gap-4 md:grid ${colsClass}`}>
          {items.map((item) => (
            <div
              key={`desktop-${item.id}`}
              className={classNames(
                'flex flex-col items-center space-y-2 rounded-2xl px-6 py-4 text-center',
                getSectionColorClass(resolved.background),
              )}
            >
              <Icon
                src={item.iconDesktop}
                size={72}
                className="text-primary m-3"
              />
              <p className="font-medium">{sub(item.title)}</p>
              <div
                className="[&_a]:text-primary text-base leading-5.5"
                dangerouslySetInnerHTML={{
                  __html: sanitize(sub(item.body)),
                }}
              />
            </div>
          ))}
        </div>

        <div className="space-y-4 md:hidden">
          {items.map((item) => (
            <Collapsible
              key={`mobile-${item.id}`}
              className={classNames(
                'rounded-2xl',
                getSectionColorClass(resolved.background),
              )}
              contentClassName="px-2 pb-3"
              header={
                <span className="flex items-center gap-3 py-1">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center">
                    <Icon src={item.iconMobile} size={24} />
                  </span>
                  <span className="text-sm font-medium">{sub(item.title)}</span>
                </span>
              }
            >
              <div
                className="[&_a]:text-primary text-sm leading-5 [&_a]:underline"
                dangerouslySetInnerHTML={{
                  __html: sanitize(sub(item.body)),
                }}
              />
            </Collapsible>
          ))}
        </div>
      </div>
    </section>
  );
};
